import { useCallback, useRef } from 'react'
import useSWR, { Fetcher } from 'swr'
import useSWRMutation, { MutationFetcher } from 'swr/mutation'
import { Schemas } from '~/api/gen'
import { useAccessTokenValue } from '~/hooks/useAccessTokenValue'

export type UseLike = {
  likeCount: number
  isLiked: boolean
  toggleLike: () => void
}

//TODO：アイテム詳細ページのAPI追加されたら、アイテム詳細ページのAPIの型追加
export const useLike = <T extends [string, ...unknown[]]>({
  key,
  id,
  getLikeId,
  createLike,
  deleteLike,
  initialLikeCount = 0,
}: {
  key: T[0]
  id: number
  getLikeId: Fetcher<number | undefined, T>
  createLike: MutationFetcher<
    number | Schemas.post_items_item_id_likes_v1_response | undefined,
    T,
    { accessToken: string; id: number }
  >
  deleteLike: MutationFetcher<
    void,
    T,
    { accessToken: string; id: number; likeId: number }
  >
  initialLikeCount?: number
}): UseLike => {
  const processing = useRef(false)
  const accessToken = useAccessTokenValue()

  const keys = [key, [id], accessToken]
  const { data: likeId } = useSWR(keys, getLikeId)
  const likeMut = useSWRMutation(keys, createLike)
  const unlikeMut = useSWRMutation(keys, deleteLike)

  const likeCount = useRef(initialLikeCount)
  const isLiked = likeId !== undefined

  const sendLike = useCallback(() => {
    if (processing.current || !accessToken) return
    likeCount.current += 1
    processing.current = true
    likeMut.trigger({ accessToken, id }).then(() => {
      processing.current = false
    })
  }, [accessToken, id, likeMut])

  const sendDeleteLike = useCallback(() => {
    if (processing.current || !accessToken || likeId === undefined) return
    likeCount.current = Math.max(0, likeCount.current - 1)
    processing.current = true
    unlikeMut.trigger({ accessToken, id, likeId }).then(() => {
      processing.current = false
    })
  }, [accessToken, id, likeId, unlikeMut])

  const toggleLike = useCallback(() => {
    if (isLiked) {
      sendDeleteLike()
    } else {
      sendLike()
    }
  }, [isLiked, sendLike, sendDeleteLike])

  return {
    likeCount: likeCount.current,
    isLiked,
    toggleLike,
  }
}
